import { Point } from "../hooks/useImageCrop";

/**
 * Replicates the computer vision team's crop calculation approach
 * @param imageDimensions - The natural dimensions of the image
 * @param targetBox - Array of corner points defining the target area
 * @param cropRelativeSize - Size of crop relative to image (default: 2, meaning crop is half the image size)
 * @returns Array of 4 corner points defining the crop area in normalized coordinates
 */
export function prepareReviewImage(
  imageDimensions: { width: number; height: number },
  targetBox: Point[],
  cropRelativeSize: number = 2,
): Point[] {
  const h = imageDimensions.height;
  const w = imageDimensions.width;
  let cropSize = Math.floor(Math.max(w, h) / cropRelativeSize);
  cropSize = Math.min(cropSize, w, h); // Ensure crop size does not exceed image dimensions
  
  // Scale corners to image dimensions and calculate center
  const centerX =
    targetBox.reduce((sum, corner) => sum + corner[0] * w, 0) /
    targetBox.length;
  const centerY =
    targetBox.reduce((sum, corner) => sum + corner[1] * h, 0) /
    targetBox.length;

  // Calculate crop bounds
  let left = Math.max(0, Math.floor(centerX - cropSize / 2));
  let right = Math.min(w, Math.floor(centerX + cropSize / 2));
  let top = Math.max(0, Math.floor(centerY - cropSize / 2));
  let bottom = Math.min(h, Math.floor(centerY + cropSize / 2));

  // Adjust if at edges
  if (left === 0) {
    right = cropSize;
  }
  if (top === 0) {
    bottom = cropSize;
  }
  if (right === w) {
    left = w - cropSize;
  }
  if (bottom === h) {
    top = h - cropSize;
  }

  // Return normalized crop corners: [[left, top], [right, top], [right, bottom], [left, bottom]]
  return [
    [left / w, top / h],
    [right / w, top / h],
    [right / w, bottom / h],
    [left / w, bottom / h],
  ];
}

/**
 * Wrapper function that works with the ImageWithBoundingBox component's data structure
 * @param points - Array of detection point objects from ImageWithBoundingBox
 * @param imageDimensions - The natural dimensions of the image
 * @param cropRelativeSize - Size of crop relative to image (default: 2)
 * @returns Array of 4 corner points defining the crop area in normalized coordinates, or null if no valid points
 */
export function calculateCVCropPoints(
  points: { points: Point[] }[],
  imageDimensions: { width: number; height: number } | null,
  cropRelativeSize: number = 2,
): Point[] | null {
  if (!points || points.length === 0 || !imageDimensions) return null;

  // Collect all valid points from all detection objects
  const allPoints: Point[] = [];
  points.forEach((pointObj) => {
    if (pointObj.points && pointObj.points.length > 0) {
      allPoints.push(...pointObj.points);
    }
  });

  if (allPoints.length === 0) return null;

  // Use the CV team's approach
  return prepareReviewImage(imageDimensions, allPoints, cropRelativeSize);
}
